import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Theme values: 'dark' | 'light' | 'auto'
// 'auto' follows the OS-level prefers-color-scheme media query.

const MEDIA_QUERY = typeof window !== 'undefined'
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null;

function getSystemTheme() {
  return MEDIA_QUERY && MEDIA_QUERY.matches ? 'dark' : 'light';
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  const effective = theme === 'auto' ? getSystemTheme() : theme;
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.remove('dark');
    root.classList.add('light');
  }
}

const useThemeStore = create(
  persist(
    (set, get) => ({
      // Default to dark to preserve the existing experience for current users
      theme: 'dark',

      // Cycle: dark → light → auto → dark...
      cycleTheme: () => {
        const order = ['dark', 'light', 'auto'];
        const i = order.indexOf(get().theme);
        const next = order[(i + 1) % order.length];
        set({ theme: next });
        applyThemeClass(next);
      },

      setTheme: (theme) => {
        set({ theme });
        applyThemeClass(theme);
      },

      // Returns the actually-applied theme (resolves 'auto' to dark/light)
      effectiveTheme: () => {
        const t = get().theme;
        return t === 'auto' ? getSystemTheme() : t;
      },
    }),
    {
      name: 'hst-theme',
      onRehydrateStorage: () => (state) => {
        if (state?.theme) applyThemeClass(state.theme);
        else applyThemeClass('dark');
      },
    }
  )
);

// Listen to OS-level theme changes when in 'auto' mode
if (MEDIA_QUERY) {
  MEDIA_QUERY.addEventListener('change', () => {
    if (useThemeStore.getState().theme === 'auto') {
      applyThemeClass('auto');
    }
  });
}

// Pre-apply on first paint to avoid flash
if (typeof window !== 'undefined') {
  try {
    const stored = JSON.parse(localStorage.getItem('hst-theme') || '{}');
    const initial = stored?.state?.theme || 'dark';
    applyThemeClass(initial);
  } catch {
    applyThemeClass('dark');
  }
}

export default useThemeStore;
